// mds-dojo — dijit/place — FAITHFUL port of references/dijit/place.js. Positions a
// DOMNode relative to a point (at) or another node (around), picking the corner where
// it's most visible in the viewport. Codemod pipeline; finished: only the final
// module `return` becomes the export (the transform's first-`return` bug hit the inner
// `return !overflow`); kernel.deprecated + domGeometry.getBorderExtents guarded (slim
// mds deps). Used by popup / dropdowns / tooltips.
import array from '../dojo/_base/array';
import domGeometry from '../dojo/dom-geometry';
import domStyle from '../dojo/dom-style';
import kernel from '../dojo/_base/kernel';
import win from '../dojo/_base/window';
import Viewport from './Viewport';
import dijit from './main';

const deprecated = (kernel && (kernel as any).deprecated) || function () { /* no-op */ };
const borderExtents = (node: any) =>
  (domGeometry as any).getBorderExtents ? (domGeometry as any).getBorderExtents(node) : { t: 0, l: 0, b: 0, r: 0 };

function _place(node: any, choices: any[], layoutNode?: any, aroundNodeCoords?: any): any {
  // Given a list of spots, put node at the first spot where it fits, else the spot
  // with the least overflow.
  const view = Viewport.getEffectiveBox(node.ownerDocument);

  if (!node.parentNode || String(node.parentNode.tagName).toLowerCase() != 'body') {
    win.body(node.ownerDocument).appendChild(node);
  }

  let best: any = null;
  array.some(choices, function (choice: any) {
    const corner = choice.corner;
    const pos = choice.pos;
    let overflow = 0;

    const spaceAvailable = {
      w: ({
        L: view.l + view.w - pos.x,
        R: pos.x - view.l,
        M: view.w,
      } as any)[corner.charAt(1)],
      h: ({
        T: view.t + view.h - pos.y,
        B: pos.y - view.t,
        M: view.h,
      } as any)[corner.charAt(0)],
    };

    const s = node.style;
    s.left = s.right = 'auto';

    if (layoutNode) {
      const res = layoutNode(node, choice.aroundCorner, corner, spaceAvailable, aroundNodeCoords);
      overflow = typeof res == 'undefined' ? 0 : res;
    }

    const style = node.style;
    const oldDisplay = style.display;
    const oldVis = style.visibility;
    if (style.display == 'none') {
      style.visibility = 'hidden';
      style.display = '';
    }
    const bb = (domGeometry as any).position(node);
    style.display = oldDisplay;
    style.visibility = oldVis;

    const startXpos = ({
      L: pos.x,
      R: pos.x - bb.w,
      M: Math.max(view.l, Math.min(view.l + view.w, pos.x + (bb.w >> 1)) - bb.w),
    } as any)[corner.charAt(1)];
    const startYpos = ({
      T: pos.y,
      B: pos.y - bb.h,
      M: Math.max(view.t, Math.min(view.t + view.h, pos.y + (bb.h >> 1)) - bb.h),
    } as any)[corner.charAt(0)];
    const startX = Math.max(view.l, startXpos);
    const startY = Math.max(view.t, startYpos);
    const endX = Math.min(view.l + view.w, startXpos + bb.w);
    const endY = Math.min(view.t + view.h, startYpos + bb.h);
    const width = endX - startX;
    const height = endY - startY;

    overflow += (bb.w - width) + (bb.h - height);

    if (best == null || overflow < best.overflow) {
      best = {
        corner: corner,
        aroundCorner: choice.aroundCorner,
        x: startX,
        y: startY,
        w: width,
        h: height,
        overflow: overflow,
        spaceAvailable: spaceAvailable,
      };
    }

    return !overflow;
  });

  if (best.overflow && layoutNode) {
    layoutNode(node, best.aroundCorner, best.corner, best.spaceAvailable, aroundNodeCoords);
  }

  let top = best.y;
  let side = best.x;
  const body = win.body(node.ownerDocument);

  if (/relative|absolute/.test(domStyle.get(body, 'position'))) {
    top -= domStyle.get(body, 'marginTop');
    side -= domStyle.get(body, 'marginLeft');
  }

  const s = node.style;
  s.top = top + 'px';
  s.left = side + 'px';
  s.right = 'auto';

  return best;
}

const reverse: any = {
  TL: 'BR',
  TR: 'BL',
  BL: 'TR',
  BR: 'TL',
};

const place: any = {
  at: function (node: any, pos: any, corners: string[], padding?: any, layoutNode?: any): any {
    const choices = array.map(corners, function (corner: string) {
      const c: any = {
        corner: corner,
        aroundCorner: reverse[corner],
        pos: { x: pos.x, y: pos.y },
      };
      if (padding) {
        c.pos.x += corner.charAt(1) == 'L' ? padding.x : -padding.x;
        c.pos.y += corner.charAt(0) == 'T' ? padding.y : -padding.y;
      }
      return c;
    });

    return _place(node, choices, layoutNode);
  },

  around: function (node: any, anchor: any, positions: any, leftToRight?: boolean, layoutNode?: any): any {
    let aroundNodePos: any;
    if (typeof anchor == 'string' || 'offsetWidth' in anchor || 'ownerSVGElement' in anchor) {
      aroundNodePos = (domGeometry as any).position(anchor, true);

      if (/^(above|below)/.test(positions[0])) {
        const anchorBorder = borderExtents(anchor),
          anchorChildBorder = anchor.firstChild ? borderExtents(anchor.firstChild) : { t: 0, l: 0, b: 0, r: 0 },
          nodeBorder = borderExtents(node),
          nodeChildBorder = node.firstChild ? borderExtents(node.firstChild) : { t: 0, l: 0, b: 0, r: 0 };
        aroundNodePos.y += Math.min(anchorBorder.t + anchorChildBorder.t, nodeBorder.t + nodeChildBorder.t);
        aroundNodePos.h -= Math.min(anchorBorder.t + anchorChildBorder.t, nodeBorder.t + nodeChildBorder.t) +
          Math.min(anchorBorder.b + anchorChildBorder.b, nodeBorder.b + nodeChildBorder.b);
      }
    } else {
      aroundNodePos = anchor;
    }

    if (anchor.parentNode) {
      let sawPosAbsolute = domStyle.getComputedStyle(anchor).position == 'absolute';
      let parent = anchor.parentNode;
      while (parent && parent.nodeType == 1 && parent.nodeName != 'BODY') {
        const parentPos = (domGeometry as any).position(parent, true),
          pcs = domStyle.getComputedStyle(parent);
        if (/relative|absolute/.test(pcs.position)) {
          sawPosAbsolute = false;
        }
        if (!sawPosAbsolute && /hidden|auto|scroll/.test(pcs.overflow)) {
          const bottomYCoord = Math.min(aroundNodePos.y + aroundNodePos.h, parentPos.y + parentPos.h);
          const rightXCoord = Math.min(aroundNodePos.x + aroundNodePos.w, parentPos.x + parentPos.w);
          aroundNodePos.x = Math.max(aroundNodePos.x, parentPos.x);
          aroundNodePos.y = Math.max(aroundNodePos.y, parentPos.y);
          aroundNodePos.h = bottomYCoord - aroundNodePos.y;
          aroundNodePos.w = rightXCoord - aroundNodePos.x;
        }
        if (pcs.position == 'absolute') {
          sawPosAbsolute = true;
        }
        parent = parent.parentNode;
      }
    }

    const x = aroundNodePos.x,
      y = aroundNodePos.y,
      width = 'w' in aroundNodePos ? aroundNodePos.w : (aroundNodePos.w = aroundNodePos.width),
      height = 'h' in aroundNodePos ? aroundNodePos.h
        : (deprecated('place.around: __Rectangle height/width deprecated; use h/w', '', '2.0'), aroundNodePos.h = aroundNodePos.height);

    const choices: any[] = [];
    function push(aroundCorner: string, corner: string) {
      choices.push({
        aroundCorner: aroundCorner,
        corner: corner,
        pos: {
          x: ({ L: x, R: x + width, M: x + (width >> 1) } as any)[aroundCorner.charAt(1)],
          y: ({ T: y, B: y + height, M: y + (height >> 1) } as any)[aroundCorner.charAt(0)],
        },
      });
    }
    array.forEach(positions, function (pos: any) {
      let ltr = leftToRight;
      switch (pos) {
        case 'above-centered': push('TM', 'BM'); break;
        case 'below-centered': push('BM', 'TM'); break;
        case 'after-centered': ltr = !ltr; /* fall through */
        // eslint-disable-next-line no-fallthrough
        case 'before-centered': push(ltr ? 'ML' : 'MR', ltr ? 'MR' : 'ML'); break;
        case 'after': ltr = !ltr; /* fall through */
        // eslint-disable-next-line no-fallthrough
        case 'before':
          push(ltr ? 'TL' : 'TR', ltr ? 'TR' : 'TL');
          push(ltr ? 'BL' : 'BR', ltr ? 'BR' : 'BL');
          break;
        case 'below-alt': ltr = !ltr; /* fall through */
        // eslint-disable-next-line no-fallthrough
        case 'below':
          push(ltr ? 'BL' : 'BR', ltr ? 'TL' : 'TR');
          push(ltr ? 'BR' : 'BL', ltr ? 'TR' : 'TL');
          break;
        case 'above-alt': ltr = !ltr; /* fall through */
        // eslint-disable-next-line no-fallthrough
        case 'above':
          push(ltr ? 'TL' : 'TR', ltr ? 'BL' : 'BR');
          push(ltr ? 'TR' : 'TL', ltr ? 'BR' : 'BL');
          break;
        default:
          push(pos.aroundCorner, pos.corner);
      }
    });

    const position = _place(node, choices, layoutNode, { w: width, h: height });
    position.aroundNodePos = aroundNodePos;

    return position;
  },
};

export default (dijit.place = place); // dijit.place set for back-compat
